export type VideoMode = 'create' | 'edit' | 'extend' | 'motion-control';
export type VideoContentMode =
  | 'grok-first-frame'
  | 'grok-images'
  | 'veo-images'
  | 'veo-frames'
  | 'minimax-multimodal'
  | 'minimax-frames'
  | 'seedance-multimodal'
  | 'seedance-frames'
  | 'gemini-references'
  | 'gemini-frames'
  | 'wan-multimodal'
  | 'wan-frames'
  | 'wan-reference'
  | 'wan-image-edit'
  | 'wan-video-edit'
  | 'wan-audio-input'
  | 'wan-first-frame';
export type VideoOrientation = 'auto' | 'video' | 'image';
export type VideoAudioSourceMode = 'preserve' | 'change';

export interface VideoLibraryAssetReference {
  assetId: string;
  jobId: string;
  providerJobId?: string | null;
  name?: string | null;
  previewUrl?: string | null;
  mime?: string | null;
  durationSeconds?: number | null;
  originModelId?: string | null;
  originProvider?: string | null;
}

export type VideoUploadField =
  | 'images'
  | 'referenceImages'
  | 'firstFrameImage'
  | 'lastFrameImage'
  | 'mainVideo'
  | 'clips'
  | 'audio'
  | 'inputVideo'
  | 'motionVideo'
  | 'characterImages';

export interface VideoUploadSpec {
  key: VideoUploadField;
  required: boolean;
  minFiles: number;
  maxFiles?: number;
  mimePrefixes: string[];
  extensions: string[];
  maxBytes?: number;
  minDurationSeconds?: number;
  maxDurationSeconds?: number;
  totalDurationMaxSeconds?: number;
  maxResolutionLabel?: string;
}

export interface VideoModelSpec {
  modelId: string;
  mode: VideoMode;
  ratios: string[];
  resolutions: string[];
  promptMax: number;
  promptRequired?: boolean;
  duration:
    | { kind: 'range'; min: number; max: number; defaultValue: number }
    | { kind: 'options'; values: number[]; defaultValue: number }
    | { kind: 'input-video' }
    | { kind: 'orientation-range'; min: number; max: number; imageMax: number; defaultValue: number };
  uploads: Partial<Record<VideoUploadField, VideoUploadSpec>>;
  audio:
    | { supported: false }
    | {
        supported: true;
        languages?: 'Multi' | string[];
        supportsSourceAudioMode?: boolean;
        saveOriginalAudioOnly?: boolean;
      };
  libraryVideo?: {
    required: boolean;
    allowedOriginModelIds: readonly string[];
    maxDurationSeconds: number;
  };
  contentModes?: readonly VideoContentMode[];
  contentModeUploadFields?: Partial<Record<VideoContentMode, readonly VideoUploadField[]>>;
  howToUse?: string;
  totalFilesMax?: number;
}

export interface VideoFileLike {
  name: string;
  mime: string;
  size: number;
  durationSeconds?: number | null;
}

export interface VideoValidationInput {
  prompt?: string;
  ratio?: string;
  resolution?: string;
  durationSeconds?: number | null;
  libraryVideo?: VideoLibraryAssetReference | null;
  contentMode?: string | null;
  orientation?: string | null;
  audioSourceMode?: string | null;
  files?: Partial<Record<VideoUploadField, VideoFileLike[]>>;
}

export interface VideoValidationError {
  field:
    | 'prompt'
    | 'ratio'
    | 'resolution'
    | 'duration'
    | 'contentMode'
    | 'orientation'
    | 'audioSourceMode'
    | 'libraryVideo'
    | VideoUploadField
    | 'totalFiles';
  type: 'required' | 'count' | 'format' | 'size' | 'duration' | 'totalDuration' | 'totalCount' | 'unsupported';
  fileName?: string;
  mime?: string;
  maxBytes?: number;
  minDurationSeconds?: number;
  maxDurationSeconds?: number;
  totalDurationMaxSeconds?: number;
  maxFiles?: number;
  totalFilesMax?: number;
  detail?: 'missingReference' | 'missingOrigin' | 'invalidOrigin' | 'missingDurationMetadata';
}

const MB = 1024 * 1024;
const IMAGE_MIME_PREFIXES = ['image/jpeg', 'image/png', 'image/webp'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const IMAGE_MAX_BYTES = 20 * MB;
const VIDEO_MIME_PREFIXES = ['video/mp4', 'video/quicktime'];
const VIDEO_EXTENSIONS = ['mp4', 'mov'];
const AUDIO_MIME_PREFIXES = ['audio/mpeg', 'audio/wav', 'audio/x-wav'];
const AUDIO_EXTENSIONS = ['mp3', 'wav'];
export const VEO_ORIGIN_MODEL_IDS = ['google:veo-3.1', 'google:veo-3.1-fast', 'google:veo-3.1-lite'] as const;
const VEO_ORIGIN_MODEL_ID_SET = new Set<string>(VEO_ORIGIN_MODEL_IDS);
export const VIDEO_MODE_MODEL_ALLOWLIST: Readonly<Record<VideoMode, readonly string[]>> = {
  create: [
    'xai:grok-1.5',
    'xai:grok-imagine',
    'google:veo-3.1',
    'google:veo-3.1-fast',
    'google:veo-3.1-lite',
    'bfl:flux-3',
    'luma:luma-ray-3.2',
    'minimax:minimax-h3',
    'minimax:minimax-h3-max',
    'minimax:hailuo-2.3',
    'minimax:hailuo-2.3-pro',
    'minimax:hailuo-2.3-fast-pro',
    'minimax:hailuo-2.3-fast',
    'kling:kling-3.0',
    'kling:kling-3.0-turbo',
    'bytedance:seedance-2.5',
    'bytedance:seedance-2.0',
    'bytedance:seedance-2.0-fast',
    'bytedance:seedance-2.0-mini',
    'google:gemini-omni-flash-1.1',
    'qwen:wan-3.0',
    'qwen:wan-3.0-prime',
    'qwen:wan-2.6',
    'qwen:wan-2.7',
  ],
  edit: [
    'bytedance:seedance-2.5-edit',
    'kling:kling-3.0-omni-edit',
    'kling:kling-o1-video-edit',
    'bfl:flux-3-video-edit',
    'xai:grok-imagine-edit',
    'google:gemini-omni-flash-1.1-edit',
  ],
  extend: ['google:veo-3.1-extend', 'google:gemini-omni-flash-1.1-extend'],
  'motion-control': ['kling:kling-3.0-motion-control', 'kling:kling-motion-control'],
} as const;

function imageUpload(
  key: VideoUploadField,
  maxFiles: number,
  options: { required?: boolean; minFiles?: number; maxBytes?: number; maxResolutionLabel?: string } = {},
): VideoUploadSpec {
  return {
    key,
    required: options.required ?? false,
    minFiles: options.minFiles ?? 0,
    maxFiles,
    mimePrefixes: IMAGE_MIME_PREFIXES,
    extensions: IMAGE_EXTENSIONS,
    maxBytes: options.maxBytes ?? IMAGE_MAX_BYTES,
    maxResolutionLabel: options.maxResolutionLabel,
  };
}

function videoUpload(
  key: VideoUploadField,
  maxFiles: number,
  options: {
    required?: boolean;
    minFiles?: number;
    maxBytes?: number;
    minDurationSeconds?: number;
    maxDurationSeconds?: number;
    totalDurationMaxSeconds?: number;
    maxResolutionLabel?: string;
  },
): VideoUploadSpec {
  return {
    key,
    required: options.required ?? false,
    minFiles: options.minFiles ?? 0,
    maxFiles,
    mimePrefixes: VIDEO_MIME_PREFIXES,
    extensions: VIDEO_EXTENSIONS,
    maxBytes: options.maxBytes,
    minDurationSeconds: options.minDurationSeconds,
    maxDurationSeconds: options.maxDurationSeconds,
    totalDurationMaxSeconds: options.totalDurationMaxSeconds,
    maxResolutionLabel: options.maxResolutionLabel,
  };
}

function audioUpload(
  maxFiles: number,
  options: {
    maxBytes: number;
    minDurationSeconds?: number;
    maxDurationSeconds?: number;
    totalDurationMaxSeconds?: number;
  },
): VideoUploadSpec {
  return {
    key: 'audio',
    required: false,
    minFiles: 0,
    maxFiles,
    mimePrefixes: AUDIO_MIME_PREFIXES,
    extensions: AUDIO_EXTENSIONS,
    maxBytes: options.maxBytes,
    minDurationSeconds: options.minDurationSeconds,
    maxDurationSeconds: options.maxDurationSeconds,
    totalDurationMaxSeconds: options.totalDurationMaxSeconds,
  };
}

export function isVeoOriginModelId(modelId: string): boolean {
  return VEO_ORIGIN_MODEL_ID_SET.has(modelId);
}

function seedanceCreateSpec(modelId: string, resolutions: string[]): VideoModelSpec {
  return {
    modelId,
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '16:9', '9:16', '21:9', 'Auto'],
    resolutions,
    promptMax: 4000,
    duration: { kind: 'range', min: 4, max: 15, defaultValue: 4 },
    contentModes: ['seedance-multimodal', 'seedance-frames'],
    contentModeUploadFields: {
      'seedance-multimodal': ['images', 'clips', 'audio'],
      'seedance-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 9, { maxBytes: 30 * MB, maxResolutionLabel: '4K' }),
      clips: videoUpload('clips', 3, {
        maxBytes: 50 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
        totalDurationMaxSeconds: 15,
        maxResolutionLabel: '4K',
      }),
      audio: audioUpload(3, {
        maxBytes: 50 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
        totalDurationMaxSeconds: 15,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
      lastFrameImage: imageUpload('lastFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true },
    totalFilesMax: 12,
  };
}

function wanCreateSpec(modelId: string): VideoModelSpec {
  return {
    modelId,
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '16:9', '9:16', 'Auto'],
    resolutions: ['480p', '720p', '1080p'],
    promptMax: 20000,
    duration: { kind: 'range', min: 2, max: 30, defaultValue: 2 },
    contentModes: ['wan-multimodal', 'wan-frames'],
    contentModeUploadFields: {
      'wan-multimodal': ['images', 'clips', 'audio'],
      'wan-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 10, { maxBytes: 30 * MB }),
      clips: videoUpload('clips', 5, {
        maxBytes: 50 * MB,
      }),
      audio: audioUpload(5, {
        maxBytes: 50 * MB,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
      lastFrameImage: imageUpload('lastFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true, languages: 'Multi' },
  };
}

function wanScenarioSpec(options: {
  modelId: string;
  promptMax: number;
  durations: { kind: 'range'; min: number; max: number; defaultValue: number } | { kind: 'options'; values: number[]; defaultValue: number };
  resolutions: string[];
  referenceFilesMax: number;
  imageEditMax: number;
}): VideoModelSpec {
  return {
    modelId: options.modelId,
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '16:9', '9:16', 'Auto'],
    resolutions: options.resolutions,
    promptMax: options.promptMax,
    duration: options.durations,
    contentModes: ['wan-reference', 'wan-image-edit', 'wan-video-edit', 'wan-audio-input', 'wan-first-frame'],
    contentModeUploadFields: {
      'wan-reference': ['images', 'clips'],
      'wan-image-edit': ['referenceImages'],
      'wan-video-edit': ['inputVideo'],
      'wan-audio-input': ['audio'],
      'wan-first-frame': ['firstFrameImage'],
    },
    uploads: {
      images: imageUpload('images', options.referenceFilesMax, { maxBytes: 30 * MB }),
      referenceImages: imageUpload('referenceImages', options.imageEditMax, { maxBytes: 30 * MB }),
      clips: videoUpload('clips', options.referenceFilesMax, {}),
      inputVideo: videoUpload('inputVideo', 1, {
        maxBytes: 100 * MB,
        minDurationSeconds: 0,
        maxDurationSeconds: 10,
      }),
      audio: audioUpload(1, {
        maxBytes: 15 * MB,
        maxDurationSeconds: 30,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true, languages: 'Multi' },
    totalFilesMax: options.referenceFilesMax,
  };
}

function editVideoSpec(options: {
  modelId: string;
  ratios: string[];
  resolutions: string[];
  promptMax: number;
  mainVideoMinSeconds: number;
  mainVideoMaxSeconds: number;
  mainVideoMaxBytes: number;
  additionalImagesMaxFiles?: number;
  additionalImagesMaxBytes?: number;
  totalFilesMax?: number;
  audio:
    | { supported: false }
    | {
        supported: true;
        languages?: 'Multi' | string[];
        supportsSourceAudioMode?: boolean;
        saveOriginalAudioOnly?: boolean;
      };
  howToUse: string;
}): VideoModelSpec {
  return {
    modelId: options.modelId,
    mode: 'edit',
    ratios: options.ratios,
    resolutions: options.resolutions,
    promptMax: options.promptMax,
    duration: { kind: 'input-video' },
    uploads: {
      mainVideo: videoUpload('mainVideo', 1, {
        required: true,
        minFiles: 1,
        maxBytes: options.mainVideoMaxBytes,
        minDurationSeconds: options.mainVideoMinSeconds,
        maxDurationSeconds: options.mainVideoMaxSeconds,
      }),
      ...(options.additionalImagesMaxFiles
        ? {
            referenceImages: imageUpload('referenceImages', options.additionalImagesMaxFiles, {
              maxBytes: options.additionalImagesMaxBytes ?? 30 * MB,
            }),
          }
        : {}),
    },
    audio: options.audio,
    totalFilesMax: options.totalFilesMax,
    howToUse: options.howToUse,
  };
}

export const VIDEO_MODEL_SPECS: readonly VideoModelSpec[] = [
  {
    modelId: 'xai:grok-1.5',
    mode: 'create',
    ratios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', 'Auto'],
    resolutions: ['480p', '720p', '1080p'],
    promptMax: 2500,
    duration: { kind: 'range', min: 1, max: 15, defaultValue: 5 },
    contentModes: ['grok-first-frame', 'grok-images'],
    contentModeUploadFields: {
      'grok-first-frame': ['firstFrameImage'],
      'grok-images': ['images'],
    },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
      images: imageUpload('images', 7),
    },
    audio: { supported: true, languages: 'Multi' },
  },
  {
    modelId: 'xai:grok-imagine',
    mode: 'create',
    ratios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', 'Auto'],
    resolutions: ['480p', '720p'],
    promptMax: 2500,
    duration: { kind: 'range', min: 1, max: 15, defaultValue: 5 },
    contentModes: ['grok-first-frame', 'grok-images'],
    contentModeUploadFields: {
      'grok-first-frame': ['firstFrameImage'],
      'grok-images': ['images'],
    },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
      images: imageUpload('images', 7),
    },
    audio: { supported: true, languages: 'Multi' },
  },
  {
    modelId: 'google:veo-3.1',
    mode: 'create',
    ratios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p', '4K'],
    promptMax: 4000,
    duration: { kind: 'options', values: [4, 6, 8], defaultValue: 4 },
    contentModes: ['veo-images', 'veo-frames'],
    contentModeUploadFields: {
      'veo-images': ['images'],
      'veo-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 3),
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true, languages: 'Multi' },
  },
  {
    modelId: 'google:veo-3.1-fast',
    mode: 'create',
    ratios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p', '4K'],
    promptMax: 4000,
    duration: { kind: 'options', values: [4, 6, 8], defaultValue: 4 },
    contentModes: ['veo-images', 'veo-frames'],
    contentModeUploadFields: {
      'veo-images': ['images'],
      'veo-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 3),
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true, languages: 'Multi' },
  },
  {
    modelId: 'google:veo-3.1-lite',
    mode: 'create',
    ratios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    promptMax: 4000,
    duration: { kind: 'options', values: [4, 6, 8], defaultValue: 4 },
    contentModes: ['veo-images', 'veo-frames'],
    contentModeUploadFields: {
      'veo-images': ['images'],
      'veo-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 3),
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true, languages: 'Multi' },
  },
  {
    modelId: 'bfl:flux-3',
    mode: 'create',
    ratios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', '9:21', 'Auto'],
    resolutions: ['720p', '1080p'],
    promptMax: 4000,
    duration: { kind: 'range', min: 5, max: 20, defaultValue: 5 },
    uploads: {
      images: imageUpload('images', 1),
    },
    audio: { supported: true },
  },
  {
    modelId: 'luma:luma-ray-3.2',
    mode: 'create',
    ratios: ['3:1', '2:1', '21:9', '16:9', '4:3', '3:2', '1:1', '3:4', '2:3', '9:16', '1:2', '1:3'],
    resolutions: ['540p', '720p', '1080p'],
    promptMax: 6000,
    duration: { kind: 'options', values: [5, 10], defaultValue: 5 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true },
  },
  {
    modelId: 'minimax:minimax-h3',
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '9:16', '16:9', '21:9', 'Auto'],
    resolutions: ['2K'],
    promptMax: 7000,
    duration: { kind: 'range', min: 5, max: 15, defaultValue: 5 },
    contentModes: ['minimax-multimodal', 'minimax-frames'],
    contentModeUploadFields: {
      'minimax-multimodal': ['images', 'clips', 'audio'],
      'minimax-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 9),
      clips: videoUpload('clips', 3, {
        maxBytes: 100 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
      }),
      audio: audioUpload(3, {
        maxBytes: 50 * MB,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: {
      supported: true,
      languages: ['Arabic', 'Chinese', 'English', 'French', 'German', 'Italian', 'Japanese', 'Korean', 'Portuguese', 'Russian', 'Spanish'],
    },
    totalFilesMax: 12,
  },
  {
    modelId: 'minimax:minimax-h3-max',
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '9:16', '16:9', '21:9', 'Auto'],
    resolutions: ['480p', '768p'],
    promptMax: 7000,
    duration: { kind: 'range', min: 5, max: 15, defaultValue: 5 },
    contentModes: ['minimax-multimodal', 'minimax-frames'],
    contentModeUploadFields: {
      'minimax-multimodal': ['images', 'clips', 'audio'],
      'minimax-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 9),
      clips: videoUpload('clips', 3, {
        maxBytes: 100 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
      }),
      audio: audioUpload(3, {
        maxBytes: 50 * MB,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: {
      supported: true,
    },
    totalFilesMax: 12,
  },
  {
    modelId: 'minimax:hailuo-2.3',
    mode: 'create',
    ratios: ['Auto'],
    resolutions: ['768p', '1080p'],
    promptMax: 2000,
    duration: { kind: 'options', values: [6, 10], defaultValue: 6 },
    uploads: {
      images: imageUpload('images', 1),
    },
    audio: { supported: false },
  },
  {
    modelId: 'minimax:hailuo-2.3-pro',
    mode: 'create',
    ratios: ['Auto'],
    resolutions: ['1080p'],
    promptMax: 2000,
    duration: { kind: 'options', values: [5], defaultValue: 5 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
    },
    audio: { supported: false },
  },
  {
    modelId: 'minimax:hailuo-2.3-fast-pro',
    mode: 'create',
    ratios: ['Auto'],
    resolutions: ['1080p'],
    promptMax: 2000,
    duration: { kind: 'options', values: [5], defaultValue: 5 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
    },
    audio: { supported: false },
  },
  {
    modelId: 'minimax:hailuo-2.3-fast',
    mode: 'create',
    ratios: ['Auto'],
    resolutions: ['768p'],
    promptMax: 2000,
    duration: { kind: 'options', values: [6, 10], defaultValue: 6 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
    },
    audio: { supported: false },
  },
  {
    modelId: 'kling:kling-3.0',
    mode: 'create',
    ratios: ['1:1', '16:9', '9:16'],
    resolutions: ['720p', '1080p', '4K'],
    promptMax: 2500,
    duration: { kind: 'options', values: [6, 10], defaultValue: 6 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true, languages: ['English', 'Chinese', 'Spanish', 'Japanese', 'Korean'] },
  },
  {
    modelId: 'kling:kling-3.0-turbo',
    mode: 'create',
    ratios: ['1:1', '16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    promptMax: 2500,
    duration: { kind: 'range', min: 5, max: 16, defaultValue: 5 },
    uploads: {
      firstFrameImage: imageUpload('firstFrameImage', 1),
      lastFrameImage: imageUpload('lastFrameImage', 1),
    },
    audio: { supported: true, languages: ['English', 'Chinese', 'Spanish', 'Japanese', 'Korean'] },
  },
  {
    modelId: 'bytedance:seedance-2.5',
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '16:9', '9:16', '21:9', 'Auto'],
    resolutions: ['480p', '720p', '1080p'],
    promptMax: 10000,
    duration: { kind: 'range', min: 4, max: 30, defaultValue: 4 },
    contentModes: ['seedance-multimodal', 'seedance-frames'],
    contentModeUploadFields: {
      'seedance-multimodal': ['images', 'clips', 'audio'],
      'seedance-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: {
        ...imageUpload('images', 30, { maxBytes: 30 * MB }),
        maxResolutionLabel: '4K',
      },
      clips: videoUpload('clips', 10, {
        maxBytes: 200 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 30,
        totalDurationMaxSeconds: 30,
        maxResolutionLabel: '4K',
      }),
      audio: audioUpload(10, {
        maxBytes: 100 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 30,
        totalDurationMaxSeconds: 30,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
      lastFrameImage: imageUpload('lastFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true, languages: 'Multi' },
    totalFilesMax: 50,
  },
  {
    modelId: 'bytedance:seedance-2.0-mini',
    mode: 'create',
    ratios: ['1:1', '3:4', '4:3', '16:9', '9:16', '21:9', 'Auto'],
    resolutions: ['480p', '720p'],
    promptMax: 4000,
    duration: { kind: 'range', min: 4, max: 15, defaultValue: 4 },
    contentModes: ['seedance-multimodal', 'seedance-frames'],
    contentModeUploadFields: {
      'seedance-multimodal': ['images', 'clips', 'audio'],
      'seedance-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: {
        ...imageUpload('images', 9, { maxBytes: 30 * MB }),
        maxResolutionLabel: '4K',
      },
      clips: videoUpload('clips', 3, {
        maxBytes: 50 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
        totalDurationMaxSeconds: 15,
        maxResolutionLabel: '4K',
      }),
      audio: audioUpload(3, {
        maxBytes: 50 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 15,
        totalDurationMaxSeconds: 15,
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
      lastFrameImage: imageUpload('lastFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true },
    totalFilesMax: 12,
  },
  seedanceCreateSpec('bytedance:seedance-2.0', ['480p', '720p', '1080p', '4K']),
  seedanceCreateSpec('bytedance:seedance-2.0-fast', ['480p', '720p']),
  {
    modelId: 'google:gemini-omni-flash-1.1',
    mode: 'create',
    ratios: ['16:9', '9:16'],
    resolutions: ['360p', '720p', '1080p', '4K'],
    promptMax: 4000,
    duration: { kind: 'range', min: 3, max: 10, defaultValue: 3 },
    contentModes: ['gemini-references', 'gemini-frames'],
    contentModeUploadFields: {
      'gemini-references': ['images', 'clips'],
      'gemini-frames': ['firstFrameImage', 'lastFrameImage'],
    },
    uploads: {
      images: imageUpload('images', 5, { maxBytes: 30 * MB, maxResolutionLabel: '4K' }),
      clips: videoUpload('clips', 3, {
        minDurationSeconds: 2,
        maxDurationSeconds: 10,
        totalDurationMaxSeconds: 10,
        maxResolutionLabel: '4K',
      }),
      firstFrameImage: imageUpload('firstFrameImage', 1, { maxBytes: 30 * MB }),
      lastFrameImage: imageUpload('lastFrameImage', 1, { maxBytes: 30 * MB }),
    },
    audio: { supported: true, languages: 'Multi' },
    totalFilesMax: 5,
  },
  wanCreateSpec('qwen:wan-3.0'),
  wanCreateSpec('qwen:wan-3.0-prime'),
  wanScenarioSpec({
    modelId: 'qwen:wan-2.7',
    promptMax: 2000,
    durations: { kind: 'range', min: 2, max: 15, defaultValue: 2 },
    resolutions: ['720p', '1080p'],
    referenceFilesMax: 5,
    imageEditMax: 4,
  }),
  wanScenarioSpec({
    modelId: 'qwen:wan-2.6',
    promptMax: 20000,
    durations: { kind: 'options', values: [5, 10, 15], defaultValue: 5 },
    resolutions: ['720p', '1080p'],
    referenceFilesMax: 1,
    imageEditMax: 1,
  }),
  editVideoSpec({
    modelId: 'kling:kling-3.0-omni-edit',
    ratios: ['Auto'],
    resolutions: ['720p', '1080p', '4K'],
    promptMax: 2500,
    mainVideoMinSeconds: 3,
    mainVideoMaxSeconds: 15,
    mainVideoMaxBytes: 200 * MB - 1,
    additionalImagesMaxFiles: 7,
    additionalImagesMaxBytes: 30 * MB - 1,
    totalFilesMax: 8,
    audio: {
      supported: true,
      languages: ['English', 'Chinese', 'Spanish', 'Japanese', 'Korean'],
      supportsSourceAudioMode: true,
    },
    howToUse:
      'Загрузите видео и опишите изменение, например: «замени человека на другого персонажа» или «смени фон». Модель хорошо сохраняет движение и звук и подходит для замены персонажей.',
  }),
  editVideoSpec({
    modelId: 'kling:kling-o1-video-edit',
    ratios: ['16:9', '9:16', '1:1'],
    resolutions: ['720p', '1080p'],
    promptMax: 2500,
    mainVideoMinSeconds: 3,
    mainVideoMaxSeconds: 15,
    mainVideoMaxBytes: 200 * MB - 1,
    additionalImagesMaxFiles: 2,
    additionalImagesMaxBytes: 30 * MB - 1,
    totalFilesMax: 3,
    audio: {
      supported: true,
      saveOriginalAudioOnly: true,
    },
    howToUse:
      'Классическое текстовое редактирование: «убери людей», «сделай ночь», «замени одежду». Предшественник Omni Edit.',
  }),
  editVideoSpec({
    modelId: 'bfl:flux-3-video-edit',
    ratios: ['Auto'],
    resolutions: ['720p', '1080p (upscale)'],
    promptMax: 4000,
    mainVideoMinSeconds: 3,
    mainVideoMaxSeconds: 20,
    mainVideoMaxBytes: 200 * MB - 1,
    audio: {
      supported: true,
      languages: 'Multi',
      supportsSourceAudioMode: true,
    },
    howToUse:
      'Опишите точечные изменения: добавление или удаление объектов, смену стиля. Модель хорошо сохраняет структуру сцены.',
  }),
  editVideoSpec({
    modelId: 'xai:grok-imagine-edit',
    ratios: ['Auto'],
    resolutions: ['480p', '720p'],
    promptMax: 3000,
    mainVideoMinSeconds: 3,
    mainVideoMaxSeconds: 20,
    mainVideoMaxBytes: 200 * MB - 1,
    audio: {
      supported: true,
      languages: 'Multi',
      supportsSourceAudioMode: true,
    },
    howToUse:
      'Загрузите видео и опишите необходимые изменения. У Grok Imagine также есть отдельный режим Extend.',
  }),
  editVideoSpec({
    modelId: 'google:gemini-omni-flash-1.1-edit',
    ratios: ['16:9', '9:16'],
    resolutions: ['360p', '720p', '1080p', '4K'],
    promptMax: 4000,
    mainVideoMinSeconds: 2,
    mainVideoMaxSeconds: 10,
    mainVideoMaxBytes: 150 * MB - 1,
    additionalImagesMaxFiles: 2,
    additionalImagesMaxBytes: 30 * MB - 1,
    totalFilesMax: 3,
    audio: {
      supported: true,
      saveOriginalAudioOnly: true,
    },
    howToUse:
      'Редактируйте видео обычным языком, например: «поменяй фон на пляж и сделай освещение закатным». Поддерживаются последовательные правки.',
  }),
  {
    modelId: 'bytedance:seedance-2.5-edit',
    mode: 'edit',
    ratios: ['Auto'],
    resolutions: ['480p', '720p'],
    promptMax: 10000,
    duration: { kind: 'input-video' },
    uploads: {
      mainVideo: {
        key: 'mainVideo',
        required: true,
        minFiles: 1,
        maxFiles: 1,
        mimePrefixes: ['video/mp4', 'video/quicktime'],
        extensions: ['mp4', 'mov'],
        maxBytes: 200 * MB,
        minDurationSeconds: 4,
        maxDurationSeconds: 30,
      },
      images: {
        key: 'images',
        required: false,
        minFiles: 0,
        maxFiles: 30,
        mimePrefixes: IMAGE_MIME_PREFIXES,
        extensions: IMAGE_EXTENSIONS,
        maxBytes: 30 * MB,
        maxResolutionLabel: '4K',
      },
      clips: {
        key: 'clips',
        required: false,
        minFiles: 0,
        maxFiles: 10,
        mimePrefixes: ['video/mp4', 'video/quicktime'],
        extensions: ['mp4', 'mov'],
        maxBytes: 200 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 30,
        totalDurationMaxSeconds: 30,
        maxResolutionLabel: '4K',
      },
      audio: {
        key: 'audio',
        required: false,
        minFiles: 0,
        maxFiles: 10,
        mimePrefixes: ['audio/mpeg', 'audio/wav', 'audio/x-wav'],
        extensions: ['mp3', 'wav'],
        maxBytes: 100 * MB,
        minDurationSeconds: 2,
        maxDurationSeconds: 30,
        totalDurationMaxSeconds: 30,
      },
    },
    audio: { supported: true, languages: 'Multi', supportsSourceAudioMode: true },
    totalFilesMax: 50,
    howToUse:
      'Загрузите видео → опишите, что изменить. Можно использовать timestamp, например: «замени фон с 0:03 по 0:08». Подходит для точечных правок без полной перегенерации.',
  },
  {
    modelId: 'google:gemini-omni-flash-1.1-extend',
    mode: 'extend',
    ratios: ['Auto'],
    resolutions: ['360p', '720p', '1080p', '4K'],
    promptMax: 5000,
    duration: { kind: 'range', min: 3, max: 10, defaultValue: 5 },
    uploads: {
      inputVideo: {
        key: 'inputVideo',
        required: true,
        minFiles: 1,
        maxFiles: 1,
        mimePrefixes: ['video/mp4', 'video/quicktime'],
        extensions: ['mp4', 'mov'],
        maxBytes: 200 * MB,
        maxDurationSeconds: 10,
      },
    },
    audio: { supported: true, languages: 'Multi' },
    howToUse:
      'Загрузите клип → выберите Extend → опишите продолжение. Удобно для наращивания историй с сохранением персонажей и движения.',
  },
  {
    modelId: 'google:veo-3.1-extend',
    mode: 'extend',
    ratios: ['Auto'],
    resolutions: ['720p'],
    promptMax: 4000,
    promptRequired: false,
    duration: { kind: 'range', min: 3, max: 7, defaultValue: 5 },
    uploads: {},
    audio: { supported: true, languages: 'Multi' },
    libraryVideo: {
      required: true,
      allowedOriginModelIds: VEO_ORIGIN_MODEL_IDS,
      maxDurationSeconds: 140,
    },
    howToUse:
      'Выберите готовое VEO-видео из библиотеки приложения → нажмите Extend → при необходимости опишите, что должно произойти дальше. Видео можно продолжать многократно.',
  },
  {
    modelId: 'kling:kling-3.0-motion-control',
    mode: 'motion-control',
    ratios: ['16:9', '9:16', '1:1'],
    resolutions: ['720p', '1080p', '4K'],
    promptMax: 3000,
    duration: { kind: 'orientation-range', min: 3, max: 30, imageMax: 10, defaultValue: 5 },
    uploads: {
      motionVideo: {
        key: 'motionVideo',
        required: true,
        minFiles: 1,
        maxFiles: 1,
        mimePrefixes: ['video/mp4', 'video/quicktime'],
        extensions: ['mp4', 'mov'],
        maxBytes: 100 * MB,
        minDurationSeconds: 3,
        maxDurationSeconds: 30,
      },
      characterImages: {
        key: 'characterImages',
        required: true,
        minFiles: 1,
        mimePrefixes: IMAGE_MIME_PREFIXES,
        extensions: IMAGE_EXTENSIONS,
      },
    },
    audio: {
      supported: true,
      languages: ['English', 'Chinese', 'Spanish', 'Japanese', 'Korean'],
      supportsSourceAudioMode: true,
    },
    howToUse:
      'Загрузите видео с движением и фото персонажа. Модель переносит движения (уровень mocap) на персонажа. Facial Element Binding помогает улучшить консистентность лица.',
  },
  {
    modelId: 'kling:kling-motion-control',
    mode: 'motion-control',
    ratios: ['1:1', '16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    promptMax: 2500,
    duration: { kind: 'range', min: 3, max: 15, defaultValue: 5 },
    uploads: {
      motionVideo: {
        key: 'motionVideo',
        required: true,
        minFiles: 1,
        maxFiles: 1,
        mimePrefixes: ['video/mp4', 'video/quicktime'],
        extensions: ['mp4', 'mov'],
        maxBytes: 100 * MB,
        minDurationSeconds: 3,
        maxDurationSeconds: 15,
      },
      characterImages: {
        key: 'characterImages',
        required: true,
        minFiles: 1,
        maxFiles: 1,
        mimePrefixes: IMAGE_MIME_PREFIXES,
        extensions: IMAGE_EXTENSIONS,
      },
    },
    audio: { supported: false },
    howToUse:
      'Загрузите видео с движением и фотографию персонажа. Модель переносит движения на персонажа на уровне motion capture. Facial Element Binding помогает улучшить консистентность лица.',
  },
] as const;

const VIDEO_MODE_MODEL_ALLOWLIST_SETS: Readonly<Record<VideoMode, ReadonlySet<string>>> = {
  create: new Set(VIDEO_MODE_MODEL_ALLOWLIST.create),
  edit: new Set(VIDEO_MODE_MODEL_ALLOWLIST.edit),
  extend: new Set(VIDEO_MODE_MODEL_ALLOWLIST.extend),
  'motion-control': new Set(VIDEO_MODE_MODEL_ALLOWLIST['motion-control']),
};
const VIDEO_MODEL_SPEC_BY_ID = new Map(VIDEO_MODEL_SPECS.map((spec) => [spec.modelId, spec]));
export const VIDEO_MODEL_IDS = new Set(VIDEO_MODEL_SPECS.map((spec) => spec.modelId));
export const VIDEO_ALLOWLIST_MODEL_IDS = new Set(
  Object.values(VIDEO_MODE_MODEL_ALLOWLIST).flatMap((ids) => ids),
);

export function getVideoModelSpec(modelId: string): VideoModelSpec | undefined {
  return VIDEO_MODEL_SPEC_BY_ID.get(modelId);
}

export function getVideoModelMode(modelId: string): VideoMode | undefined {
  return getVideoModelSpec(modelId)?.mode;
}

export function getVideoModelAllowlistForMode(mode: VideoMode): readonly string[] {
  return VIDEO_MODE_MODEL_ALLOWLIST[mode];
}

export function isVideoModelAllowedForMode(modelId: string, mode: VideoMode): boolean {
  const spec = getVideoModelSpec(modelId);
  return Boolean(spec && spec.mode === mode && VIDEO_MODE_MODEL_ALLOWLIST_SETS[mode].has(modelId));
}

export function isVideoModelInAllowlist(modelId: string): boolean {
  const mode = getVideoModelMode(modelId);
  return Boolean(mode && isVideoModelAllowedForMode(modelId, mode));
}

export function pickCompatibleVideoModelForMode(
  mode: VideoMode,
  availableModelIds: readonly string[],
  currentModelId?: string,
): string | undefined {
  const candidates = new Set(
    availableModelIds.filter((modelId) => isVideoModelAllowedForMode(modelId, mode)),
  );
  if (currentModelId && candidates.has(currentModelId)) return currentModelId;
  for (const modelId of VIDEO_MODE_MODEL_ALLOWLIST[mode]) {
    if (candidates.has(modelId)) return modelId;
  }
  return undefined;
}

export function getVideoModelsForMode(mode: VideoMode): VideoModelSpec[] {
  return VIDEO_MODEL_SPECS.filter((spec) => spec.mode === mode);
}

export function getVideoDurationLimit(spec: VideoModelSpec, orientation?: string | null): { min?: number; max?: number } {
  if (spec.duration.kind === 'range') return { min: spec.duration.min, max: spec.duration.max };
  if (spec.duration.kind === 'options') {
    return {
      min: spec.duration.values[0],
      max: spec.duration.values[spec.duration.values.length - 1],
    };
  }
  if (spec.duration.kind === 'orientation-range') {
    return {
      min: spec.duration.min,
      max: orientation === 'image' ? spec.duration.imageMax : spec.duration.max,
    };
  }
  return {};
}

function getActiveUploadFields(spec: VideoModelSpec, contentMode?: string | null): VideoUploadField[] {
  if (contentMode && spec.contentModeUploadFields) {
    const fields = spec.contentModeUploadFields[contentMode as VideoContentMode];
    if (fields) return [...fields];
  }
  return Object.keys(spec.uploads) as VideoUploadField[];
}

export function validateVideoInput(spec: VideoModelSpec, input: VideoValidationInput): VideoValidationError[] {
  const errors: VideoValidationError[] = [];
  const prompt = input.prompt ?? '';
  if (prompt.length > spec.promptMax) {
    errors.push({ field: 'prompt', type: 'unsupported' });
  }
  if (!spec.ratios.includes(input.ratio ?? '')) {
    errors.push({ field: 'ratio', type: 'unsupported' });
  }
  if (!spec.resolutions.includes(input.resolution ?? '')) {
    errors.push({ field: 'resolution', type: 'unsupported' });
  }
  if (spec.contentModes?.length && input.contentMode && !spec.contentModes.includes(input.contentMode as VideoContentMode)) {
    errors.push({ field: 'contentMode', type: 'unsupported' });
  }
  if (
    spec.duration.kind === 'orientation-range' &&
    input.orientation &&
    !(['auto', 'video', 'image'] as const).includes(input.orientation as VideoOrientation)
  ) {
    errors.push({ field: 'orientation', type: 'unsupported' });
  }
  if (
    spec.audio.supported &&
    !spec.audio.saveOriginalAudioOnly &&
    spec.audio.supportsSourceAudioMode &&
    input.audioSourceMode
  ) {
    if (!(['preserve', 'change'] as const).includes(input.audioSourceMode as VideoAudioSourceMode)) {
      errors.push({ field: 'audioSourceMode', type: 'unsupported' });
    }
  }

  if (spec.libraryVideo) {
    const libraryVideo = input.libraryVideo ?? null;
    if (!libraryVideo) {
      if (spec.libraryVideo.required) errors.push({ field: 'libraryVideo', type: 'required' });
    } else {
      const assetId = typeof libraryVideo.assetId === 'string' ? libraryVideo.assetId.trim() : '';
      const jobId = typeof libraryVideo.jobId === 'string' ? libraryVideo.jobId.trim() : '';
      if (!assetId || !jobId) {
        errors.push({ field: 'libraryVideo', type: 'unsupported', detail: 'missingReference' });
      }

      const originModelId =
        typeof libraryVideo.originModelId === 'string' ? libraryVideo.originModelId.trim() : '';
      const originProvider =
        typeof libraryVideo.originProvider === 'string' ? libraryVideo.originProvider.trim() : '';
      if (!originModelId || !originProvider) {
        errors.push({ field: 'libraryVideo', type: 'unsupported', detail: 'missingOrigin' });
      } else if (
        originProvider !== 'google' ||
        !spec.libraryVideo.allowedOriginModelIds.includes(originModelId)
      ) {
        errors.push({ field: 'libraryVideo', type: 'unsupported', detail: 'invalidOrigin' });
      }

      const durationSeconds = Number(libraryVideo.durationSeconds ?? NaN);
      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        errors.push({
          field: 'libraryVideo',
          type: 'unsupported',
          detail: 'missingDurationMetadata',
        });
      } else if (durationSeconds > spec.libraryVideo.maxDurationSeconds) {
        errors.push({
          field: 'libraryVideo',
          type: 'duration',
          maxDurationSeconds: spec.libraryVideo.maxDurationSeconds,
        });
      }
    }
  }

  const durationLimit = getVideoDurationLimit(spec, input.orientation);
  if (spec.duration.kind !== 'input-video') {
    const duration = Number(input.durationSeconds ?? 0);
    if (!Number.isFinite(duration) || duration <= 0 || (durationLimit.min && duration < durationLimit.min) || (durationLimit.max && duration > durationLimit.max)) {
      errors.push({
        field: 'duration',
        type: 'duration',
        minDurationSeconds: durationLimit.min,
        maxDurationSeconds: durationLimit.max,
      });
    }
  }

  const files = input.files ?? {};
  const activeUploadFields = getActiveUploadFields(spec, input.contentMode);
  for (const field of activeUploadFields) {
    const upload = spec.uploads[field];
    if (!upload) continue;
    const selected = files[field] ?? [];
    if (upload.required && selected.length < upload.minFiles) {
      errors.push({ field, type: 'required' });
      continue;
    }
    if (selected.length < upload.minFiles) {
      errors.push({ field, type: 'count', maxFiles: upload.maxFiles });
    }
    if (upload.maxFiles !== undefined && selected.length > upload.maxFiles) {
      errors.push({ field, type: 'count', maxFiles: upload.maxFiles });
    }
    let durationTotal = 0;
    let durationKnownCount = 0;
    for (const file of selected) {
      if (!matchesVideoFile(upload, file)) {
        errors.push({ field, type: 'format', fileName: file.name, mime: file.mime });
      }
      if (upload.maxBytes !== undefined && file.size > upload.maxBytes) {
        errors.push({ field, type: 'size', fileName: file.name, maxBytes: upload.maxBytes });
      }
      if (file.durationSeconds != null) {
        durationKnownCount += 1;
        durationTotal += file.durationSeconds;
        if (
          (upload.minDurationSeconds !== undefined && file.durationSeconds < upload.minDurationSeconds) ||
          (upload.maxDurationSeconds !== undefined && file.durationSeconds > upload.maxDurationSeconds)
        ) {
          errors.push({
            field,
            type: 'duration',
            fileName: file.name,
            minDurationSeconds: upload.minDurationSeconds,
            maxDurationSeconds: upload.maxDurationSeconds,
          });
        }
      }
    }
    if (
      upload.totalDurationMaxSeconds !== undefined &&
      selected.length > 0 &&
      durationKnownCount === selected.length &&
      durationTotal > upload.totalDurationMaxSeconds
    ) {
      errors.push({
        field,
        type: 'totalDuration',
        totalDurationMaxSeconds: upload.totalDurationMaxSeconds,
      });
    }
  }

  if (spec.totalFilesMax !== undefined) {
    const totalFiles = activeUploadFields.reduce((sum, field) => sum + (files[field]?.length ?? 0), 0);
    if (totalFiles > spec.totalFilesMax) {
      errors.push({ field: 'totalFiles', type: 'totalCount', totalFilesMax: spec.totalFilesMax });
    }
  }

  return dedupeValidationErrors(errors);
}

export function normalizeVideoJobOptions(
  spec: VideoModelSpec,
  options: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {
    section: 'video',
    videoMode: spec.mode,
    ratio: spec.ratios.includes(String(options.ratio ?? '')) ? options.ratio : spec.ratios[0],
    resolution: spec.resolutions.includes(String(options.resolution ?? '')) ? options.resolution : spec.resolutions[0],
  };

  if (spec.duration.kind === 'range' || spec.duration.kind === 'orientation-range') {
    const limit = getVideoDurationLimit(spec, typeof options.orientation === 'string' ? options.orientation : undefined);
    const duration = Number(options.durationSeconds ?? options.duration ?? 0);
    if (Number.isFinite(duration) && duration > 0 && (!limit.min || duration >= limit.min) && (!limit.max || duration <= limit.max)) {
      normalized.durationSeconds = duration;
    }
  } else if (spec.duration.kind === 'options') {
    const duration = Number(options.durationSeconds ?? options.duration ?? 0);
    if (spec.duration.values.includes(duration)) normalized.durationSeconds = duration;
  }

  if (spec.duration.kind === 'orientation-range' && ['auto', 'video', 'image'].includes(String(options.orientation ?? ''))) {
    normalized.orientation = options.orientation;
  }

  if (spec.contentModes?.includes(String(options.contentMode ?? '') as VideoContentMode)) {
    normalized.contentMode = options.contentMode;
  }

  if (spec.libraryVideo) {
    const libraryVideo = normalizeVideoLibraryAssetReference(options.libraryVideo);
    if (libraryVideo) normalized.libraryVideo = libraryVideo;
  }

  const activeUploadFields = getActiveUploadFields(
    spec,
    typeof normalized.contentMode === 'string' ? normalized.contentMode : undefined,
  );

  if (spec.audio.supported && !spec.audio.saveOriginalAudioOnly) {
    if (options.audioEnabled === true) normalized.audioEnabled = true;
    if (
      options.audioEnabled === true &&
      spec.audio.supportsSourceAudioMode &&
      ['preserve', 'change'].includes(String(options.audioSourceMode ?? ''))
    ) {
      normalized.audioSourceMode = options.audioSourceMode;
    }
  }

  for (const field of activeUploadFields) {
    const upload = spec.uploads[field];
    if (!upload) continue;
    const files = Array.isArray(options[field]) ? options[field] : [];
    const normalizedFiles = files
      .filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')
      .map((value) => ({
        name: typeof value.name === 'string' ? value.name : 'file',
        mime: typeof value.mime === 'string' ? value.mime : '',
        size: typeof value.size === 'number' ? value.size : Number(value.size ?? 0),
        durationSeconds:
          typeof value.durationSeconds === 'number' ? value.durationSeconds : value.durationSeconds == null ? undefined : Number(value.durationSeconds),
      }))
      .slice(0, upload.maxFiles ?? files.length);
    if (normalizedFiles.length > 0) normalized[field] = normalizedFiles;
  }

  return normalized;
}

function normalizeVideoLibraryAssetReference(value: unknown): VideoLibraryAssetReference | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const assetId = typeof record.assetId === 'string' ? record.assetId.trim() : '';
  const jobId = typeof record.jobId === 'string' ? record.jobId.trim() : '';
  if (!assetId || !jobId) return undefined;

  return {
    assetId,
    jobId,
    providerJobId:
      typeof record.providerJobId === 'string' && record.providerJobId.trim()
        ? record.providerJobId.trim()
        : null,
    name: typeof record.name === 'string' && record.name.trim() ? record.name.trim() : null,
    previewUrl:
      typeof record.previewUrl === 'string' && record.previewUrl.trim() ? record.previewUrl.trim() : null,
    mime: typeof record.mime === 'string' && record.mime.trim() ? record.mime.trim() : null,
    durationSeconds:
      typeof record.durationSeconds === 'number'
        ? record.durationSeconds
        : record.durationSeconds == null
          ? undefined
          : Number(record.durationSeconds),
    originModelId:
      typeof record.originModelId === 'string' && record.originModelId.trim()
        ? record.originModelId.trim()
        : null,
    originProvider:
      typeof record.originProvider === 'string' && record.originProvider.trim()
        ? record.originProvider.trim()
        : null,
  };
}

function matchesVideoFile(upload: VideoUploadSpec, file: VideoFileLike): boolean {
  const lowerName = file.name.toLowerCase();
  const ext = lowerName.includes('.') ? lowerName.split('.').pop() ?? '' : '';
  return upload.mimePrefixes.includes(file.mime) || upload.extensions.includes(ext);
}

function dedupeValidationErrors(errors: VideoValidationError[]): VideoValidationError[] {
  const seen = new Set<string>();
  const result: VideoValidationError[] = [];
  for (const error of errors) {
    const key = JSON.stringify(error);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(error);
  }
  return result;
}
